// main.ts
import {
  loadRuntimeConfig,
  getRuntimeLobbyUrl,
  isPartnerFromSearch,
  getLobbyUrlFromGameQuery,
  getApiUrlFromGameQuery,
} from '@config/runtime.config';
import { resolveLobbyRedirectUrl } from '@config/lobby-redirect.config';
import { Assets } from 'pixi.js';
import { PixiCanvas } from '@core/Canvas/PixiCanvas';
import { AssetManager } from '@core/Assets';
import { AudioPlayer } from '@core/Audio/AudioPlayer';
import { AssetService } from '@core/Assets';
import { OutboundService } from '@core/Web/services/OutboundService';
import { StompClient } from '@core/Web/clients/StompClient';
import { getGameFromUrl } from '@core/utils/urlParser';
import { applyLanguageFromUrl } from '@core/i18n';
import { maybeResetAudioStorageAfterReload } from '@core/Audio/audioStorage';
import { GameLaunchService } from '@core/Web/services/GameLaunchService';
import { getAuthToken } from '@core/utils/authToken';
import { clearGameSession, getGameSessionId } from '@core/utils/gameSession';
import { waitForAccessToken } from '@core/utils/resolveAccessToken';
import { API_CONFIG } from '@core/Web/config/api.config';
import { getGameLaunchMeta } from '@config/game-launch.config';
import {
  isOwnCanvasRoute,
  isSkyxNewRoute,
  syncBodyDataGameAttribute,
} from '@core/utils/gameRouteSlugs';
import { SKYX_NEW_SOUND_VOLUMES } from '@core/gameengine/config/skyxNewSound.config';
import { clearSkyxNewGameReady } from '@core/utils/skyxNewBootstrap';
import { getActiveGameStoreOrNull } from '@core/gameengine/state/activeGameStore';
import { getGameRegistryEntry } from '@config/game-registry';
import { ensureGameStylesLoaded } from '@config/manifest-loader.runtime';
import { getStaticAssetOrigin } from '@core/utils/assetFolderSlug';
import { initNewRelicUserSync } from '@core/utils/syncNewRelicUserId';
import { initAnalytics } from '@core/analytics';
import { initPostMessageHandler } from '@core/utils/postMessageHandler';
import { initRegulatoryPolicy, useGamingSessionStore } from '@core/regulatory';
import { readJurisdictionFromEnvironment } from '@core/regulatory';

let canvas: PixiCanvas | null = null;
let assetManager: AssetManager | null = null;
let assetService: AssetService | null = null;
let audioPlayer: AudioPlayer | null = null;
let outboundService: OutboundService | null = null;
let currentGame: any = null;
let currentSlug: string | null = null;
let isNavigating = false;

maybeResetAudioStorageAfterReload();

Assets.init({});
initNewRelicUserSync();
initAnalytics();
initPostMessageHandler();
async function createAudioForGame(slug: string) {
  const entry = getGameRegistryEntry(slug);
  if (!entry) {
    return audioPlayer;
  }

  if (audioPlayer) {
    audioPlayer.dispose();
    audioPlayer = null;
  }

  const assetModule = entry.loadAssets ? await entry.loadAssets() : null;
  const soundMap =
    assetModule?.CRASH_ASSETS?.sounds ??
    assetModule?.AVIATOR_ASSETS?.sounds ??
    {};
  const soundVolumes =
    assetModule?.NFLKICK_SOUND_VOLUMES ??
    (isSkyxNewRoute(slug) ? SKYX_NEW_SOUND_VOLUMES : undefined);

  return new AudioPlayer(
    {
      slug: entry.audioSlug ?? slug,
      assetManager: assetManager!,
      soundMap,
      ...(soundVolumes ? { soundVolumes } : {}),
      ...(isSkyxNewRoute(slug)
        ? {
            duckMusicOnSfx: true,
            musicDuckRatio: 0.2,
          }
        : {}),
    },
    'howler'
  );
}

async function createControllerForGame(slug: string) {
  const entry = getGameRegistryEntry(slug);
  if (!entry) {
    throw new Error('Unsupported game: ' + slug);
  }

  ensureGameStylesLoaded(entry.routeSlug);
  audioPlayer = await createAudioForGame(slug);
  const { CrashGameController } = await entry.loadController();
  const controller = new CrashGameController(outboundService!, audioPlayer!);

  controller.initialize({
    stage: canvas!,
    assetManager: assetManager!,
    audioPlayer: audioPlayer!,
    assetService: assetService!,
    outboundService: outboundService!,
    assetBaseSlug: entry.audioSlug ?? slug,
  });

  return controller;
}

async function ensureStage() {
  if (!canvas) {
    const stageHost = document.getElementById('stage');
    if (!stageHost) {
      console.error(
        '[PlayGrid] Missing #stage element. Cannot mount game canvas.'
      );
      return;
    }

    const slug = parseSlug();
    Object.assign(stageHost.style, {
      position: 'fixed',
      inset: '0',
      zIndex: '1',
      pointerEvents: 'none',
      display: slug && !isOwnCanvasRoute(slug) ? 'block' : 'none',
    });

    const rootEl = document.getElementById('root');
    if (rootEl)
      Object.assign(rootEl.style, {
        position: 'fixed',
        inset: '0',
        zIndex: '10',
        pointerEvents: 'none',
      });

    const gameHudEl = document.getElementById('gamehud');
    if (gameHudEl)
      Object.assign(gameHudEl.style, {
        inset: '0',
        zIndex: '20',
        pointerEvents: 'none',
      });

    const gameCanvas = document.getElementById('game-canvas');
    if (!gameCanvas) {
      console.error(
        '[PlayGrid] Missing #game-canvas element. Cannot mount game canvas.'
      );
      return;
    }

    console.log('[PlayGrid] Found #game-canvas element:', gameCanvas);
    console.log('[PlayGrid] Canvas container dimensions:', {
      width: gameCanvas.offsetWidth,
      height: gameCanvas.offsetHeight,
      position: getComputedStyle(gameCanvas).position,
    });

    canvas = new PixiCanvas();
    await canvas.mount(gameCanvas);

    console.log('[PlayGrid] Canvas mounted to #game-canvas');
    console.log(
      '[PlayGrid] Canvas container children after mount:',
      gameCanvas.children
    );
  }

  if (!assetManager) {
    const baseUrl = getStaticAssetOrigin();
    console.log('[PlayGrid] AssetManager baseUrl:', baseUrl);
    assetManager = new AssetManager(baseUrl);
  }
  if (!assetService) {
    const baseUrl = getStaticAssetOrigin();
    console.log('[PlayGrid] AssetService baseUrl:', baseUrl);
    assetService = AssetService.getInstance(baseUrl);
    // Initialize AssetService for the crash game
    await assetService.initializeGame('crash');
  }

  if (!outboundService) {
    const stompClient = new StompClient();
    stompClient.init();
    const stompAdaptor = stompClient.adaptor();
    outboundService = new OutboundService(stompAdaptor);
    console.log('[PlayGrid] OutboundService initialized with StompAdaptor');
  }
}

async function launchGameSession(slug: string): Promise<void> {
  const meta = getGameLaunchMeta(slug);
  if (!meta) {
    console.warn(
      '[PlayGrid] No launch config for slug, skipping launch API:',
      slug
    );
    return;
  }

  const authToken = await waitForAccessToken();
  if (!authToken) {
    throw new Error('[PlayGrid] Cannot launch game without an access token.');
  }

  clearGameSession();
  const response = await GameLaunchService.getInstance().launchGame(slug);
  if (!getGameSessionId()) {
    throw new Error(
      '[PlayGrid] Game launch completed but gameSessionId was not stored.'
    );
  }

  const launchJurisdiction =
    typeof response.launchParams?.jurisdiction === 'string'
      ? response.launchParams.jurisdiction
      : readJurisdictionFromEnvironment();
  initRegulatoryPolicy(launchJurisdiction);

  console.log('[PlayGrid] Game launch successful:', {
    gameId: response.gameId,
    gameSessionId: response.gameSessionId,
    playerSessionId: response.playerSessionId,
    jurisdiction: launchJurisdiction,
  });
}

async function mountGame(slug: string) {
  const isSkyxNew = isSkyxNewRoute(slug);
  if (isSkyxNew) {
    clearSkyxNewGameReady();
  }

  const overlay = isSkyxNew ? null : document.createElement('div');
  if (overlay) {
    overlay.id = 'pg-loading';
    Object.assign(overlay.style, {
      position: 'fixed',
      inset: '0',
      display: 'grid',
      placeItems: 'center',
      background: 'rgba(0,0,0)',
      zIndex: '9999999',
      pointerEvents: 'auto',
    });
    document.body.appendChild(overlay);
  }

  if (currentSlug === slug && currentGame) {
    console.debug('[PlayGrid] mountGame skipped; already on', slug);
    overlay?.remove();
    return;
  }

  const authToken = await waitForAccessToken();
  if (!authToken) {
    throw new Error('[PlayGrid] Cannot mount game without an access token.');
  }

  await ensureStage();
  try {
    await launchGameSession(slug);

    const stageHost = document.getElementById('stage');
    if (stageHost && !isOwnCanvasRoute(slug)) {
      stageHost.style.display = 'block';
    }

    if (getGameRegistryEntry(slug)) {
      currentGame = await createControllerForGame(slug);
      overlay?.remove();
    } else {
      throw new Error('Unsupported game: ' + slug);
    }

    const gameCanvas = document.getElementById(
      'game-canvas'
    ) as HTMLDivElement | null;
    if (!gameCanvas) throw new Error('Missing #game-canvas element');
    await currentGame.mount(gameCanvas);
    currentSlug = slug;
    // UKGC session timer starts later via tryStartFreshGamingSession when the
    // game UI is actually shown (connected + skyx ready) — not at mount.
  } catch (err) {
    overlay?.remove();
    console.error('[PlayGrid] Failed to mount game', slug, err);
    getActiveGameStoreOrNull()?.getState()?.setConnectionStatus?.('failed');
    showErrorOverlay('Failed to load game. Please try again.', () =>
      navigateFromURL()
    );
  }
}

async function unmountGame() {
  if (currentGame) {
    try {
      await currentGame.unmount();
    } catch (err) {
      console.warn('[PlayGrid] Error during game unmount', err);
    } finally {
      currentGame = null;
      currentSlug = null;
    }
  }
  if (audioPlayer) {
    audioPlayer.dispose();
    audioPlayer = null;
  }
  clearSkyxNewGameReady();
  clearGameSession();
  useGamingSessionStore.getState().clearGamingSession('unmountGame');
  const stageHost = document.getElementById('stage');
  if (stageHost) stageHost.style.display = 'none';
}

function navEvent() {
  window.dispatchEvent(new Event('playgrid:navigation'));
}
function parseSlug() {
  // Use the new URL parser that supports both path and query params
  return getGameFromUrl();
}

async function navigateFromURL() {
  if (isNavigating) return;
  isNavigating = true;
  applyLanguageFromUrl();
  const slug = parseSlug();
  try {
    if (slug) {
      const authToken = await waitForAccessToken(1500);
      if (!authToken) {
        console.debug(
          '[PlayGrid] navigateFromURL: defer mount until auth is ready'
        );
        return;
      }
      if (currentSlug !== slug) {
        await unmountGame();
        await mountGame(slug);
      } else {
        console.debug('[PlayGrid] navigateFromURL: already on', slug);
      }
    } else {
      await unmountGame();
    }
  } finally {
    syncBodyDataGameAttribute(slug);
    navEvent();
    isNavigating = false;
  }
}

function showErrorOverlay(message: string, onRetry?: () => void) {
  let el = document.getElementById('pg-error');
  if (!el) {
    el = document.createElement('div');
    el.id = 'pg-error';
    Object.assign(el.style, {
      position: 'fixed',
      inset: '0',
      display: 'grid',
      placeItems: 'center',
      background: 'rgba(0,0,0,.45)',
      zIndex: '9999',
      pointerEvents: 'auto',
    });
    document.body.appendChild(el);
  }
  el.innerHTML = `<div style="background:#1c2228;color:#e8f0f8;padding:16px 20px;border-radius:12px;box-shadow:0 8px 30px rgba(0,0,0,.4);font-family:system-ui,sans-serif;">
    <div style="font-weight:700;margin-bottom:8px;">PlayGrid</div>
    <div>${message}</div>
    <div style="margin-top:12px;text-align:right;">
      <button id="pg-retry" style="padding:6px 10px;border-radius:8px;background:#2b3945;color:#fff;border:0;cursor:pointer;pointer-events:auto;">Retry</button>
    </div>
  </div>`;
  const btn = document.getElementById('pg-retry');
  btn?.addEventListener(
    'click',
    () => {
      el?.remove();
      onRetry?.();
    },
    { once: true }
  );
}

(window as any).playgrid = {
  async startGame(slug: string) {
    const url = new URL(location.href);
    url.searchParams.set('game', slug);
    history.pushState({}, '', url);
    await navigateFromURL();
  },
  async goLobby() {
    const isPartner = isPartnerFromSearch();
    // Prefer ?lobbyUrl= from the game URL for both redirect and postMessage target.
    const targetUrl = resolveLobbyRedirectUrl(
      window.location.hostname,
      getRuntimeLobbyUrl()
    );
    let postMessageTargetOrigin = targetUrl;
    try {
      postMessageTargetOrigin = new URL(targetUrl).origin;
    } catch {
      /* keep full targetUrl */
    }
    const gameId = currentGame?.getGameState?.()?.gameId;

    // 1. Check environment: Is it an iframe?
    const isIframe = window.self !== window.top;

    // 2. Check environment: Is it NOT mobile?
    const userAgent = navigator.userAgent;
    const isMobileUA =
      /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
    const touchPoints = navigator.maxTouchPoints || 0;
    const isNotMobile = !isMobileUA && touchPoints === 0;

    // --- DEBUG LOGS ---
    console.group('[PlayGrid] Environment Check');
    console.log('portalUrl / lobbyUrl:', targetUrl);
    console.log('isPartner:', isPartner);
    console.log('Is Iframe:', isIframe);
    console.log('Is Not Mobile (Calculated):', isNotMobile);
    console.log('User Agent:', userAgent);
    console.log('Max Touch Points:', touchPoints);
    console.log('Detected as Mobile via UA:', isMobileUA);
    console.log('Game ID:', gameId);
    console.groupEnd();

    // Sites launched with ?lobbyUrl= — redirect only (no postMessage).
    // Avoids parent shells that JSON.parse(event.data) on object payloads.
    if (isPartner) {
      try {
        const authToken = getAuthToken();
        if (gameId && authToken) {
          console.log('[PlayGrid] Attempting API end-session call...');
          await fetch(
            `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.gameEnd(gameId)}`,
            {
              method: 'POST',
              headers: {
                accept: '*/*',
                authorization: `Bearer ${authToken}`,
                'content-type': 'application/json',
              },
            }
          );
          console.log('[PlayGrid] API end-session call successful.');
        }
      } catch (err) {
        console.warn('[PlayGrid] Failed to end game session via API:', err);
      } finally {
        clearGameSession();
      }

      console.log('[PlayGrid] Redirecting to lobbyUrl from query:', targetUrl);
      window.location.href = targetUrl;
      return;
    }

    if (isIframe && isNotMobile && gameId) {
      console.log(
        '[PlayGrid] Sending postMessage to:',
        postMessageTargetOrigin
      );

      window.parent.postMessage(
        {
          type: 'GAME_SESSION_ENDED',
          gameId: gameId,
          timestamp: new Date().toISOString(),
        },
        postMessageTargetOrigin
      );
    } else {
      console.log(
        '[PlayGrid] postMessage skipped. Reason:',
        !isIframe
          ? 'Not in an iframe.'
          : !isNotMobile
            ? 'Device identified as mobile/touch.'
            : !gameId
              ? 'Game ID missing.'
              : 'Unknown context.'
      );

      try {
        const authToken = getAuthToken();
        if (gameId && authToken) {
          console.log('[PlayGrid] Attempting API end-session call...');
          await fetch(
            `${API_CONFIG.baseUrl}${API_CONFIG.endpoints.gameEnd(gameId)}`,
            {
              method: 'POST',
              headers: {
                accept: '*/*',
                authorization: `Bearer ${authToken}`,
                'content-type': 'application/json',
              },
            }
          );
          console.log('[PlayGrid] API end-session call successful.');
        }
      } catch (err) {
        console.warn('[PlayGrid] Failed to end game session via API:', err);
      } finally {
        clearGameSession();
      }

      console.log('[PlayGrid] Redirecting to:', targetUrl);
      window.location.href = targetUrl;
    }
  },
  async unmountGame() {
    await unmountGame();
  },
  get outboundService() {
    return outboundService;
  },
  get currentGame() {
    return currentGame;
  },
  getCurrentGameController() {
    return currentGame;
  },
};

window.addEventListener('popstate', () => {
  navigateFromURL();
});
// Don't automatically navigate on page load - let App.tsx control this
// navigateFromURL();

async function bootstrap() {
  await loadRuntimeConfig();
  // Persist launch ?apiUrl= / ?lobbyUrl= in same-origin sessionStorage.
  getApiUrlFromGameQuery();
  getLobbyUrlFromGameQuery();
  await import('./index');
}

void bootstrap();
